import { Link } from 'react-router-dom';
import { companies } from '../../mocks/companies';
import { useState } from 'react';
import { useTikTokThumbnail } from '../../hooks/useTikTokThumbnail';

function SessionCompanyCard({ company }: { company: typeof companies[0] }) {
  const { thumbnailUrl } = useTikTokThumbnail(company.tiktokVideoId);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Video Thumbnail */}
        <Link 
          to={`/company/${company.id}`}
          className="flex-shrink-0 cursor-pointer"
        >
          <div className="relative rounded-xl overflow-hidden w-full md:w-40 aspect-[9/16] bg-gray-100">
            <img 
              src={thumbnailUrl || company.videoThumbnail} 
              alt={company.name}
              className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-white">
              {company.industry}
            </div>
            {company.hasLiveToday && (
              <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                LIVE
              </div>
            )}
          </div>
        </Link>

        {/* Company Info */}
        <div className="flex-1">
          <Link to={`/company/${company.id}`} className="cursor-pointer">
            <h3 className="text-xl font-bold mb-2 hover:text-gray-600 transition-colors">
              {company.name}
            </h3>
          </Link>
          <p className="text-gray-600 mb-4">{company.editorNote}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {company.tags.map((tag, idx) => (
              <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>

          {/* Schedule */}
          <div className="space-y-2">
            {company.upcomingSessions.map((session, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-black text-white rounded-lg">
                    <i className="ri-calendar-line text-lg"></i>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{session.date} {session.time}</p>
                    <p className="text-xs text-gray-500">{session.platform}</p>
                  </div>
                </div>
                <Link
                  to={`/company/${company.id}`}
                  className="px-4 py-2 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  詳細を見る
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SessionsPage() {
  const [filter, setFilter] = useState<'all' | 'today' | 'week'>('all');

  const liveToday = companies.filter(c => c.hasLiveToday);
  const liveThisWeek = companies.filter(c => 
    c.upcomingSessions.some(s => s.date.includes('今週') || s.date === '今夜')
  );

  const displayCompanies = filter === 'today' 
    ? liveToday 
    : filter === 'week' 
    ? liveThisWeek 
    : companies.filter(c => c.upcomingSessions.length > 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/bf28291cc5d7f0a9c8281f40e0310854/bac760dd5111dec7a1acf46dcaf57f3b.jpeg" 
              alt="縦長ジョブ" 
              className="h-60 w-auto"
            />
          </Link>
          <Link 
            to="/" 
            className="text-sm text-gray-600 hover:text-black cursor-pointer"
          >
            ← ホームへ
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 px-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h1 className="text-4xl md:text-5xl font-bold">説明会一覧</h1>
          </div>
          <p className="text-lg text-gray-600">
            今すぐ見られる会社説明会をチェック
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 px-4 border-b border-gray-100 sticky top-[73px] bg-white z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === 'all' 
                  ? 'bg-black text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              すべて
            </button>
            <button
              onClick={() => setFilter('today')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === 'today' 
                  ? 'bg-black text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              今夜開催
            </button>
            <button
              onClick={() => setFilter('week')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === 'week' 
                  ? 'bg-black text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              今週開催
            </button>
          </div>
        </div>
      </section>

      {/* Sessions List */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <p className="text-gray-600">
              {displayCompanies.length}件の説明会
            </p>
          </div>
          <div className="space-y-6">
            {displayCompanies.map((company) => (
              <SessionCompanyCard key={company.id} company={company} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/bf28291cc5d7f0a9c8281f40e0310854/bac760dd5111dec7a1acf46dcaf57f3b.jpeg" 
                alt="縦長ジョブ" 
                className="h-48 w-auto mb-4"
              />
              <p className="text-sm text-gray-400">
                縦動画で見つける、新しい仕事との出会い
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">サイトマップ</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/" className="hover:text-white cursor-pointer">ホーム</Link></li>
                <li><Link to="/sessions" className="hover:text-white cursor-pointer">説明会一覧</Link></li>
                <li><Link to="/privacy" className="hover:text-white cursor-pointer">プライバシーポリシー</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">対象業界</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>飲食</li>
                <li>小売</li>
                <li>物流</li>
                <li>製造</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>&copy; 2025 縦長ジョブ. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}